import SelectComponent from "@via/components/select/select.tsx";
import { Control, useController } from "react-hook-form";
import { useUserGroups } from "@via/api/user-groups.api.tsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { SelectOption } from "@via/utils/interfaces";
import { GetUserGroupsResponse } from "common";

interface UserGroupSelectComponentProps {
  name: string;
  errors?: Record<string, any>;
  rules?: any;
  control?: Control<any>;
  menuPlacement?: "top" | "bottom";
  isDisabled?: boolean;
  onChange?: (value: SelectOption | null) => void;
  missingId?: (string | number)[];
  isMulti?: boolean;
  enableClear?: boolean;
  uniqueIdentifier?: "vmetaGroupId" | "id";
  disableIfNotFound?: boolean; // Optional prop to transform select to input and make disable
  placeholder?: string;
}

const UserGroupSelectComponent = ({
  name,
  control,
  errors,
  rules,
  menuPlacement = "top",
  isDisabled = false,
  onChange,
  missingId = [],
  isMulti = false,
  enableClear = false,
  uniqueIdentifier = "id",
  disableIfNotFound = false,
  placeholder = "Select a group",
}: UserGroupSelectComponentProps) => {
  const controller = control ? useController({ name, control }) : null;

  const { field, formState } = controller || {};

  const fieldValue = field?.value;

  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [search, setSearch] = useState("");
  const [cumulativeUserGroups, setCumulativeUserGroups] = useState<
    GetUserGroupsResponse[]
  >([]);
  const [missingUserGroups, setMissingUserGroups] = useState<
    GetUserGroupsResponse[]
  >([]);
  const [value, setValue] = useState<SelectOption[]>([]);

  const { data: userGroups, isLoading: isUserGroupsLoading } = useUserGroups(
    page,
    pageSize,
    [],
    [{ id: "groupName", value: search }]
  );

  // Fetch missing selected user group separately
  const {
    data: missingUserGroupsData,
    isLoading: isMissingUserGroupsDataLoading,
  } = useUserGroups(
    0,
    100, // large number to fetch all missing selected user groups at once
    [], // no sort
    [{ id: uniqueIdentifier, value: missingId ?? [] }],
    missingId.length ? true : false // only fetch if there is missing user group
  );

  useEffect(() => {
    if (missingUserGroupsData?.data) {
      setMissingUserGroups(missingUserGroupsData.data);
    }
  }, [missingUserGroupsData]);

  const fetchMoreUserGroups = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((userGroups?.total ?? 0) / pageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [userGroups?.total]);

  useEffect(() => {
    if (userGroups?.data) {
      setCumulativeUserGroups(prevUserGroups => {
        // Append new results while preventing duplicates
        const existingIds = new Set(
          prevUserGroups.map(p => p[uniqueIdentifier])
        );
        const newUserGroups = userGroups?.data?.filter(
          p => !existingIds.has(p[uniqueIdentifier])
        );

        return [...prevUserGroups, ...newUserGroups];
      });
    }
  }, [userGroups?.data, search, page]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === search) return;
    setPage(0);
    setSearch(value);
  }, 300);

  const userGroupOptions = useMemo(() => {
    const map = new Map();
    cumulativeUserGroups.forEach(userGroup => {
      map.set(userGroup[uniqueIdentifier], userGroup);
    });
    missingUserGroups.forEach(userGroup => {
      map.set(userGroup[uniqueIdentifier], userGroup);
    });
    const mergedUserGroups: GetUserGroupsResponse[] = Array.from(map.values());

    if (missingId.length) {
      const missingUserGroups = mergedUserGroups.filter(userGroup =>
        missingId.includes(userGroup[uniqueIdentifier])
      );

      if (missingUserGroups.length) {
        setValue(
          missingUserGroups.map(missingUserGroup => ({
            label: missingUserGroup.name,
            value: missingUserGroup[uniqueIdentifier],
          }))
        );
      }
    }

    return (
      mergedUserGroups.map(userGroup => ({
        label: userGroup.name,
        value: userGroup[uniqueIdentifier],
      })) ?? []
    );
  }, [cumulativeUserGroups, missingUserGroups]);

  const currentValueExists = userGroupOptions.some(
    opt => opt.value === fieldValue
  );

  const hasSelectedValue = Array.isArray(fieldValue)
    ? fieldValue.length > 0
    : fieldValue != null;

  if (control) {
    if (
      disableIfNotFound &&
      hasSelectedValue &&
      !(isUserGroupsLoading || isMissingUserGroupsDataLoading) &&
      !currentValueExists
    ) {
      const [obj, index] = name.split(".");
      const { groupName } = formState?.defaultValues?.[obj][index] || {};

      return (
        <input
          type="text"
          className="w-full block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          disabled={true}
          value={groupName || ""}
        />
      );
    } else {
      return (
        <>
          <SelectComponent
            control={control}
            name={name}
            rules={rules}
            menuPlacement={menuPlacement}
            readOnly={isDisabled}
            options={userGroupOptions}
            isLoading={isUserGroupsLoading || isMissingUserGroupsDataLoading}
            placeholder={placeholder}
            onFetchMoreOptions={fetchMoreUserGroups}
            onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
            isMulti={isMulti}
            enableClear={enableClear}
          />
          {errors?.groupId && (
            <span className="text-xs text-rose-500">
              {errors.groupId.message}
            </span>
          )}
        </>
      );
    }
  } else {
    return (
      <SelectComponent
        name={name}
        menuPlacement={menuPlacement}
        readOnly={isDisabled}
        options={userGroupOptions}
        value={value}
        onChange={val => onChange?.(val as SelectOption)}
        isLoading={isUserGroupsLoading || isMissingUserGroupsDataLoading}
        placeholder={placeholder}
        onFetchMoreOptions={fetchMoreUserGroups}
        onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
        isMulti={isMulti}
        enableClear={enableClear}
      />
    );
  }
};

export default UserGroupSelectComponent;
